import { useEffect } from 'react';
import { X } from 'lucide-react';

import { useUiStore } from '../../store/uiStore.js';

export function Panel({ title, actions, children, bodyClass = '', ...rest }) {
  return (
    <section className="panel" {...rest}>
      {(title || actions) && (
        <header className="panel-head">
          {typeof title === 'string' ? <h3>{title}</h3> : title}
          {actions && <div className="row gap-1">{actions}</div>}
        </header>
      )}
      <div className={`panel-body ${bodyClass}`}>{children}</div>
    </section>
  );
}

export function Stat({ value, label, sub, tone }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value" style={tone ? { color: tone } : undefined}>
        {value}
      </span>
      {sub && <span className="stat-sub">{sub}</span>}
    </div>
  );
}

export function Meter({ value, total, className = '' }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div
      className={`meter ${className}`}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={total}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Keycap({ children }) {
  return <kbd className="keycap">{children}</kbd>;
}

export function EmptyState({ title, children, action }) {
  return (
    <div className="empty-state">
      {title && <h3>{title}</h3>}
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="loading-page">
      <div className="spinner" />
      {label && <p className="small muted">{label}</p>}
    </div>
  );
}

export function Modal({ title, description, onClose, children, footer, width }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        style={width ? { width } : undefined}
      >
        <header className="modal-head">
          <div>
            {typeof title === 'string' ? <h3>{title}</h3> : title}
            {description && <p className="small muted" style={{ marginTop: 4 }}>{description}</p>}
          </div>
          <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  );
}

export function ToastHost() {
  const toasts = useUiStore((s) => s.toasts);
  const dismiss = useUiStore((s) => s.dismiss);
  if (!toasts.length) return null;

  return (
    <div className="toast-host" role="status" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast ${toast.tone}`}>
          <span>{toast.message}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action.onClick();
                dismiss(toast.id);
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/** Coarse relative time - exact enough for "saved 4s ago" without a date library. */
export function relativeTime(timestamp) {
  if (!timestamp) return '';
  const then = typeof timestamp === 'number' ? timestamp : Date.parse(timestamp);
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((Date.now() - then) / 1000);
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(then).toLocaleDateString();
}

export function SaveStatus({ state, onRetry }) {
  const { status, at, error } = state;
  const text =
    status === 'dirty'
      ? 'Unsaved changes'
      : status === 'saving'
        ? 'Saving…'
        : status === 'saved'
          ? `Saved ${relativeTime(at)}`
          : status === 'error'
            ? error?.message || 'Save failed'
            : 'Up to date';

  return (
    <span className={`save-status ${status}`} role="status" aria-live="polite">
      <span className="save-dot" />
      {text}
      {status === 'error' && (
        <button type="button" className="btn sm" onClick={onRetry}>
          Retry
        </button>
      )}
    </span>
  );
}
